import { FunctionComponent, useCallback, useEffect, useId, useMemo, useState } from "react";

import classnames from "classnames";

import { AlertType } from "@ppb/the-wall-common/types";

import { GenericIcon } from "@ppb/the-wall-icons/GenericIcon/GenericIcon";
import { SystemIconName, ValueIconName } from "@ppb/the-wall-icons";

import { Alert, FreeBets, Link, PrimaryButton } from "@ppb/the-wall-web";
import { BetsSummary } from "../../../BetsSummary/BetsSummary.web";

import { PlaceFooterViewModel } from "./PlaceFooter.types";

import { SportsbookJurisdictionalOperatorInfo } from "../../../JurisdictionalOperatorInfo/JurisdictionalOperatorInfo.web";
import { AnimatedDots } from "../../../AnimatedDots/AnimatedDots.web";
import { SecondaryLabelCarousel } from "./SecondaryLabelCarousel/SecondaryLabelCarousel.web";

const FADEOUT_TIME = 500;

import styles from "./PlaceFooter.web.css";

export const PlaceFooter: FunctionComponent<PlaceFooterViewModel> = ({
  i18n,
  isPanelDisabled,
  isPlaceDisabled,
  notifications,
  footerPrefix,
  termsUrl,

  // Accept Odds Movement
  showAcceptOddsMovementAlert,
  isOddsMovementOn,
  oddsMovementLabels,

  // Free Bets
  hasFreeBets,
  isFreeBetsSelected,
  isFreeBetsDisabled = false,

  // Free Bets Wallets
  freeBetsAlertMessage,

  // Summary
  isSummaryDisabled,
  balanceAfterBet,
  totalReturns,
  totalOriginalReturns,
  isOddsBoosted = false,

  // Buttons
  hasCTALoading = true,
  hasPlaceError,
  isPlaceBlocked = false,
  placeBtnLabel,
  placeBtnSecondaryLabel,
  placeBtnLoadingLabel,
  reversePlaceBtnLabels = false,
  isLoggedIn = false,
  secondaryButton,

  // Actions
  onFreeBetsChange,
  onFreeBetsRemovePress,
  onRemoveAllPress,
  onPlacePress,
  onOddsMovementPreferencesChange,
}) => {
  const [shouldStopAnimation, setShouldStopAnimation] = useState(false);
  const [prevHasPlaceError, setPrevHasPlaceError] = useState(hasPlaceError);
  const oddsMovementSwitchId = useId();

  // Derive shouldStopAnimation during render when hasPlaceError transitions to true.
  // This avoids the cascading render from calling setState synchronously in an effect.
  if (hasPlaceError !== prevHasPlaceError) {
    setPrevHasPlaceError(hasPlaceError);
    if (hasPlaceError) {
      setShouldStopAnimation(true);
    }
  }

  // The issue is that hasPlaceError stays true after the error, so stopAnimation remains true.
  // In ActionButton, triggerTap only starts loading when !stopAnimation, so subsequent taps can't retrigger the isLoading state.
  // The fix is to make stopAnimation transient: set it true when a place error occurs, then reset it to false after the fade-out completes (matching ActionButton's FADEOUT_TIME of 500ms).
  useEffect(() => {
    if (!shouldStopAnimation) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      setShouldStopAnimation(false);
    }, FADEOUT_TIME);

    return () => clearTimeout(timeout);
  }, [shouldStopAnimation]);

  const iconTrashColor = isPanelDisabled
    ? "var(--place-footer-icon-disable-colour)"
    : "var(--place-footer-icon-default-colour)";

  const freeBetsContainer = classnames({
    [styles.freeBetsContainer]: hasFreeBets || freeBetsAlertMessage,
  });

  const handleOddsMovementChange = useCallback(
    (isChecked: boolean) => {
      onOddsMovementPreferencesChange(isChecked);
    },
    [onOddsMovementPreferencesChange],
  );

  const handlePlacePress = useCallback(() => {
    if (isPlaceBlocked) {
      setShouldStopAnimation(true);
    }
    onPlacePress();
  }, [isPlaceBlocked, onPlacePress]);

  const oddsMovementAlertAction = useMemo(
    () => ({
      checkboxId: oddsMovementSwitchId,
      onChange: handleOddsMovementChange,
      isChecked: isOddsMovementOn,
      label: oddsMovementLabels.message,
    }),
    [oddsMovementSwitchId, handleOddsMovementChange, isOddsMovementOn, oddsMovementLabels.message],
  );

  const hasSecondaryLabelCarousel = Array.isArray(placeBtnSecondaryLabel) && placeBtnSecondaryLabel.length > 0;

  return (
    <footer className={styles.footerContainer}>
      {notifications}
      <div className={freeBetsContainer}>
        {hasFreeBets && (
          <FreeBets
            label={i18n.freeBetsLabel}
            isSelected={isFreeBetsSelected}
            disabled={isPanelDisabled || isFreeBetsDisabled}
            onFreeBetsChange={onFreeBetsChange}
          />
        )}
        {!!freeBetsAlertMessage && (
          <Alert
            message={freeBetsAlertMessage}
            type={AlertType.Generosity}
            onClose={(!isPanelDisabled && onFreeBetsRemovePress) || undefined}
            showCloseIcon={false}
            iconOverload={ValueIconName.FREE_BET}
            dismissLabel={(!isPanelDisabled && i18n.freeBetsAlertRemoveLabel) || undefined}
          />
        )}
      </div>
      {showAcceptOddsMovementAlert && isLoggedIn && (
        <div className={styles.oddsMovementAlertContainer}>
          <Alert
            message={oddsMovementLabels.message}
            detail={oddsMovementLabels.detailMessage}
            type={AlertType.Info}
            showCloseIcon={false}
            action={oddsMovementAlertAction}
          />
        </div>
      )}
      {footerPrefix}
      <div className={styles.summaryContainer}>
        <BetsSummary
          disabled={isPanelDisabled || isSummaryDisabled}
          totalStake={balanceAfterBet ?? ""}
          totalStakeLabel={balanceAfterBet ? i18n.balanceAfterBet : ""}
          totalReturns={totalReturns}
          totalOriginalReturns={totalOriginalReturns}
          totalReturnsLabel={i18n.totalReturns}
          isOddsBoosted={isOddsBoosted}
        />
        <SportsbookJurisdictionalOperatorInfo />
        <div className={styles.actions}>
          {secondaryButton || (
            <button className={styles.removeButton} type="button" disabled={isPanelDisabled} onClick={onRemoveAllPress}>
              <span className={styles.screenReaderOnly}>{i18n.removeLabel}</span>
              <div className={styles.trashIcon}>
                <GenericIcon name={SystemIconName.TRASH} color={iconTrashColor} />
              </div>
            </button>
          )}
          <PrimaryButton
            disabled={isPanelDisabled || isPlaceDisabled}
            label={placeBtnLabel}
            secondaryLabel={!Array.isArray(placeBtnSecondaryLabel) ? placeBtnSecondaryLabel : undefined}
            onTap={handlePlacePress}
            loadingLabel={hasCTALoading ? placeBtnLoadingLabel : undefined}
            loadingChildren={isLoggedIn && <AnimatedDots />}
            stopAnimation={shouldStopAnimation}
            variant={isLoggedIn ? "transactional" : "primary"}
            reverseLabels={reversePlaceBtnLabels}
          >
            {hasSecondaryLabelCarousel && <SecondaryLabelCarousel messages={placeBtnSecondaryLabel} />}
          </PrimaryButton>
        </div>
        {!!termsUrl && (
          <div className={styles.termsContainer}>
            {i18n.termsLabel}{" "}
            <Link onClick={() => {}} item={{ viewLink: { viewUrl: termsUrl }, target: "_blank" }}>
              {i18n.termsLinkLabel}
            </Link>
          </div>
        )}
      </div>
    </footer>
  );
};
